(() => {
  const COMPANY_ID = 'Tgdd7G';
  const REVISION = '2026-07-15';
  const TIMEOUT_MS = 6000;
  const TOAST_DURATION_MS = 6000;
  const SHEETS_URL = 'https://script.google.com/macros/s/AKfycbwu0-jUmeq1NjZlzJ7oC89SaKxM0nG9TGJWxcBHvDi83DbaZ3FlntqzLaXvi66TfDDNdA/exec';
  const LIST_IDS = {
    'Contact Form': 'SqjGWP',
    'Speaker Application': 'RC49Dq',
    'Exhibitor Application': 'WUGxPt',
    'Ninja Application': 'WQ5hu5',
    'Partner Application': 'YxRUDa'
  };

  const showToast = (text, type = 'success') => {
    if (!text) return;
    let region = document.querySelector('.msm-form-toasts');
    if (!region) {
      region = document.createElement('div');
      region.className = 'msm-form-toasts';
      region.setAttribute('aria-live', 'polite');
      document.body.appendChild(region);
    }

    const toast = document.createElement('div');
    toast.className = `msm-form-toast msm-form-toast--${type}`;
    toast.setAttribute('role', type === 'error' ? 'alert' : 'status');
    toast.textContent = text;
    region.appendChild(toast);
    requestAnimationFrame(() => toast.classList.add('is-visible'));
    setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.addEventListener('transitionend', () => toast.remove(), { once: true });
    }, TOAST_DURATION_MS);
  };

  const showPostedResult = () => {
    const params = new URLSearchParams(location.search);
    if (params.get('contact_posted') === 'true') {
      showToast('Thank you. Your form has been submitted successfully.');
      history.replaceState({}, '', `${location.pathname}${location.hash}`);
      return;
    }

    const error = document.querySelector('[data-klaviyo-form] .errors');
    if (error) showToast(error.textContent.trim() || 'Please check the form and try again.', 'error');
  };

  const contactFields = (form) => {
    const fields = {};
    new FormData(form).forEach((value, name) => {
      if (!name.startsWith('contact[') || value instanceof File) return;
      const key = name.slice(8, -1);
      if (!key || key === 'tags' || key === 'status') return;
      fields[key] = fields[key] ? `${fields[key]}, ${value}` : String(value).trim();
    });
    return fields;
  };

  const profileAttributes = (fields, formType, includeProperties = true) => {
    const { email, ...customProperties } = fields;
    const profile = { email };
    if (includeProperties) profile.properties = { ...customProperties, msm_source: formType };
    if (fields.first_name) profile.first_name = fields.first_name;
    if (fields.last_name) profile.last_name = fields.last_name;
    // Keep phone in event properties: Klaviyo rejects profile phone identifiers unless they are E.164.
    if (fields.location) profile.location = { address1: fields.location };
    if (fields.name && !fields.first_name) profile.first_name = fields.name;
    return profile;
  };

  const klaviyoRequest = async (path, payload) => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), TIMEOUT_MS);
    try {
      const response = await fetch(`https://a.klaviyo.com${path}?company_id=${encodeURIComponent(COMPANY_ID)}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', revision: REVISION },
        body: JSON.stringify(payload),
        signal: controller.signal
      });
      if (!response.ok) throw new Error(`Klaviyo ${response.status}: ${(await response.text()).slice(0, 300)}`);
    } finally {
      clearTimeout(timer);
    }
  };

  const sendEvent = (fields, formType) => {
    const payload = {
      data: {
        type: 'event',
        attributes: {
          metric: { data: { type: 'metric', attributes: { name: `Submitted ${formType}` } } },
          profile: { data: { type: 'profile', attributes: profileAttributes(fields, formType) } },
          properties: { ...fields, form_type: formType, page_url: location.href },
          time: new Date().toISOString()
        }
      }
    };

    return klaviyoRequest('/client/events/', payload);
  };

  const subscribeToList = (fields, formType) => {
    const listId = LIST_IDS[formType];
    if (!listId) return Promise.resolve();
    return klaviyoRequest('/client/subscriptions/', {
      data: {
        type: 'subscription',
        attributes: {
          custom_source: `Shopify ${formType}`,
          profile: { data: { type: 'profile', attributes: profileAttributes(fields, formType, false) } }
        },
        relationships: { list: { data: { type: 'list', id: listId } } }
      }
    });
  };

  const sendToKlaviyo = async (fields, formType) => {
    const results = await Promise.allSettled([
      sendEvent(fields, formType),
      subscribeToList(fields, formType)
    ]);
    const failures = results.filter((result) => result.status === 'rejected');
    if (failures.length) throw failures[0].reason;
  };

  const sendToSheets = (fields, formType) => {
    const body = JSON.stringify({ ...fields, form_type: formType, page_url: location.href });
    if (navigator.sendBeacon?.(SHEETS_URL, new Blob([body], { type: 'text/plain;charset=UTF-8' }))) return;
    fetch(SHEETS_URL, { method: 'POST', mode: 'no-cors', keepalive: true, body });
  };

  const connect = (form) => {
    const button = form.querySelector('button[type="submit"], input[type="submit"]');
    let submitting = false;
    let validationToastShown = false;

    const setLoading = () => {
      if (!button) return;
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
      if (button.tagName === 'INPUT') {
        button.dataset.defaultLabel = button.value;
        button.value = button.dataset.loadingLabel || 'Submitting...';
        return;
      }
      button.dataset.defaultHtml = button.innerHTML;
      const label = button.dataset.loadingLabel || button.textContent.trim() || 'Submit';
      button.innerHTML = `<span>${label}</span><span class="msm-form-spinner" aria-hidden="true"></span>`;
    };

    const resetLoading = () => {
      if (!button) return;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      if (button.tagName === 'INPUT') button.value = button.dataset.defaultLabel || 'Submit';
      else if (button.dataset.defaultHtml) button.innerHTML = button.dataset.defaultHtml;
    };
    form.addEventListener('invalid', () => {
      if (validationToastShown) return;
      validationToastShown = true;
      showToast('Please complete all required fields before submitting.', 'error');
      setTimeout(() => { validationToastShown = false; }, 1000);
    }, true);

    form.addEventListener('submit', async (event) => {
      if (form.dataset.klaviyoHandover === 'true') return;
      if (!form.checkValidity()) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      if (submitting) return;
      submitting = true;
      setLoading();

      const fields = contactFields(form);
      const formType = fields.form_type || form.dataset.klaviyoForm || 'Contact Form';
      if (fields.email) {
        try {
          sendToSheets(fields, formType);
        } catch (error) {
          console.error('[msm-sheets] Submission failed; continuing with Shopify submit', error);
        }

        try {
          await sendToKlaviyo(fields, formType);
        } catch (error) {
          console.error('[msm-klaviyo] Event failed; continuing with Shopify submit', error);
        }
      }

      form.dataset.klaviyoHandover = 'true';
      try {
        form.requestSubmit();
      } catch (error) {
        resetLoading();
        submitting = false;
        form.dataset.klaviyoHandover = 'false';
        showToast('Unable to submit the form. Please try again.', 'error');
        console.error('[msm-form] Shopify submit failed', error);
      }
    }, true);
  };

  document.addEventListener('DOMContentLoaded', () => {
    document.querySelectorAll('form[data-klaviyo-form]').forEach(connect);
    showPostedResult();
  });
})();
